/**
 * Mastery Week 2 (W2) — buildReviewQueue
 *
 * Produce a prioritised list of StudentKP to review in this session.
 *
 * Priority (head -> tail):
 *   1. overdue KPs, by overdue ratio desc:  (now - dueTime) / interval(box)
 *   2. due KPs (overdue ratio === 0),       newer due first (smaller delta first)
 *   3. new KPs (never attempted)            by kpId asc
 *
 * INVARIANTS:
 *   - pure function. No external state.
 *   - never mutates input array or its elements.
 *   - 學習與遊戲分離 — no imports from src/game/.
 *
 * Schema note: real StudentKP uses lastReviewedISO (string). We parse to epoch ms
 * so the `now: number` contract matches the rest of the W2 API surface.
 *
 * Interval table per task spec (NOT the K-3-tuned research version):
 *   box1=1d, box2=3d, box3=7d, box4=14d, box5=30d
 */

import type { LeitnerBox, StudentKP } from '../../shared/types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Leitner interval (days) per box — task-spec values. */
const LEITNER_INTERVAL_DAYS: Record<LeitnerBox, number> = {
  1: 1,
  2: 3,
  3: 7,
  4: 14,
  5: 30,
};

/** Default queue size — K-3 attention window (8–12 min × ~30s/題) ≈ 20. */
const DEFAULT_MAX_QUEUE_SIZE = 20;

export interface BuildReviewQueueOptions {
  /** Cap the returned queue length. Defaults to 20. */
  maxQueueSize?: number;
  /** When true, drop "new" KPs (never attempted). Defaults to false. */
  includeDueOnly?: boolean;
}

/**
 * Whether a KP is "new" (never reviewed).
 * StudentKP.lastReviewedISO === '' means never reviewed (per shared/types.ts L421).
 */
function isNewKP(kp: StudentKP): boolean {
  return kp.totalAttempts === 0 || kp.lastReviewedISO === '';
}

/**
 * Parse ISO timestamp to epoch ms. Returns NaN for empty / invalid input —
 * callers must guard with `isNewKP` first.
 */
function parseIso(iso: string): number {
  if (!iso) return Number.NaN;
  return Date.parse(iso);
}

/** Interval in ms for a given box. */
function intervalMs(box: LeitnerBox): number {
  return LEITNER_INTERVAL_DAYS[box] * MS_PER_DAY;
}

/**
 * Overdue ratio. > 0 = overdue, === 0 = exactly due, < 0 = not yet due.
 * Higher ratio = more urgent.
 */
function overdueRatio(kp: StudentKP, now: number): number {
  const last = parseIso(kp.lastReviewedISO);
  const interval = intervalMs(kp.box);
  const dueAt = last + interval;
  return (now - dueAt) / interval;
}

/**
 * Build a prioritised review queue from a student's full KP list.
 *
 * @param kps   immutable input
 * @param now   epoch ms (inject for tests)
 * @param opts  optional cap + filter
 */
export function buildReviewQueue(
  kps: ReadonlyArray<StudentKP>,
  now: number,
  opts: BuildReviewQueueOptions = {},
): StudentKP[] {
  const maxQueueSize = opts.maxQueueSize ?? DEFAULT_MAX_QUEUE_SIZE;
  const includeDueOnly = opts.includeDueOnly ?? false;

  if (maxQueueSize <= 0) return [];

  const reviewed: StudentKP[] = [];
  const fresh: StudentKP[] = [];

  for (const kp of kps) {
    if (isNewKP(kp)) {
      if (!includeDueOnly) fresh.push(kp);
      continue;
    }
    reviewed.push(kp);
  }

  // Partition reviewed into due-or-overdue vs not-yet-due.
  const dueOrOverdue: Array<{ kp: StudentKP; ratio: number }> = [];
  for (const kp of reviewed) {
    const ratio = overdueRatio(kp, now);
    if (ratio >= 0) dueOrOverdue.push({ kp, ratio });
    // not-yet-due: drop from queue (we only surface due/overdue/new).
  }

  // Sort overdue/due by ratio desc (most urgent first). Stable tiebreak: kpId asc.
  dueOrOverdue.sort((a, b) => {
    if (b.ratio !== a.ratio) return b.ratio - a.ratio;
    return a.kp.kpId.localeCompare(b.kp.kpId);
  });

  // New KPs: deterministic kpId asc ordering.
  fresh.sort((a, b) => a.kpId.localeCompare(b.kpId));

  // Compose: due/overdue first, then new KPs.
  const composed: StudentKP[] = [
    ...dueOrOverdue.map((entry) => entry.kp),
    ...fresh,
  ];

  return composed.slice(0, maxQueueSize);
}
